import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/kakaoAuth.css";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import axios from "axios";

const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 정규식 패턴
const phoneRegex = /^010\d{8}$/; // 010으로 시작하는 11자리 숫자
const birthdateRegex = /^\d{8}$/; // 8자리 숫자 (YYYYMMDD)

function KakaoAuthPage() {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    birthdate: "",
  });

  // 에러 상태 추가
  const [errors, setErrors] = useState({
    name: false,
    phone: false,
    birthdate: false,
  });

  // 에러 메시지
  const [errorMessages, setErrorMessages] = useState({
    name: "이름을 입력해주세요.",
    phone: "010으로 시작하는 11자리 번호를 입력해주세요.",
    birthdate: "생년월일 8자리를 입력해주세요. (예: 19900101)",
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  // 입력 필드 ref 추가
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const birthdateInputRef = useRef(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [receiptID, setReceiptID] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const statusCheckIntervalRef = useRef(null);
  const didReturnFromKakaoRef = useRef(false);
  const lastVisibilityChangeTime = useRef(Date.now());
  const checkAttemptsRef = useRef(0);
  // 최대 체크 시도 횟수 수정 (10분 = 약 300회, 2초 간격)
  const maxCheckAttempts = 300;
  const verificationSuccessRef = useRef(false);
  const returnTimestampRef = useRef(null);

  // 인증 중 타이머 관련 상태 추가
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showTimer, setShowTimer] = useState(false);
  const timerIntervalRef = useRef(null);

  // 요청 중단을 위한 컨트롤러 추가
  const abortControllerRef = useRef(null);

  // 펜딩 상태 관리를 위한 ref 추가
  const pendingCheckRef = useRef(false);
  const pendingCheckTimeoutRef = useRef(null);

  // 페이지 애니메이션 변형 설정
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 30,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: "tween",
        ease: "easeOut",
        duration: 0.5,
      },
    },
    exit: {
      opacity: 0,
      y: -30,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 페이지 가시성 변화 감지 (카카오톡 앱에서 돌아올 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();

      if (document.visibilityState === "visible") {
        const savedReceiptID = localStorage.getItem("kakao_receipt_id");
        const authInProgress = localStorage.getItem("kakao_auth_in_progress");

        if (authInProgress === "true" && savedReceiptID) {
          didReturnFromKakaoRef.current = true;
          returnTimestampRef.current = now;

          // receiptID 복원
          if (savedReceiptID) {
            setReceiptID(savedReceiptID);
          }

          // 타이머 재시작
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }

          timerIntervalRef.current = setInterval(() => {
            setElapsedTime((prev) => prev + 1);
          }, 1000);

          setShowTimer(true);
          setCheckingStatus(true);

          // 앱에서 돌아왔을 때 즉시 체크 실행
          checkSignatureStatus(savedReceiptID, true, true);
        }
      }

      lastVisibilityChangeTime.current = now;
    };

    // visibilitychange 이벤트 리스너 등록
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 모바일 이벤트 추가 (iOS Safari 등에서 더 잘 작동)
    const handleFocus = () => {
      const savedReceiptID = localStorage.getItem("kakao_receipt_id");
      const authInProgress = localStorage.getItem("kakao_auth_in_progress");

      if (authInProgress === "true" && savedReceiptID) {
        didReturnFromKakaoRef.current = true;
        returnTimestampRef.current = Date.now();

        setCheckingStatus(true);
        // 앱에서 돌아왔을 때 즉시 체크 실행
        checkSignatureStatus(savedReceiptID, true, true);
      }
    };

    const handlePageShow = (event) => {
      const savedReceiptID = localStorage.getItem("kakao_receipt_id");
      const authInProgress = localStorage.getItem("kakao_auth_in_progress");

      if (authInProgress === "true" && savedReceiptID) {
        didReturnFromKakaoRef.current = true;
        returnTimestampRef.current = Date.now();

        setCheckingStatus(true);
        // 앱에서 돌아왔을 때 즉시 체크 실행
        checkSignatureStatus(savedReceiptID, true, true);
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handlePageShow);

    // 모바일 전용 추가 이벤트
    window.addEventListener("popstate", () => {
      const savedReceiptID = localStorage.getItem("kakao_receipt_id");
      const authInProgress = localStorage.getItem("kakao_auth_in_progress");

      if (authInProgress === "true" && savedReceiptID) {
        // startPeriodicChecks(savedReceiptID);
      }
    });

    // 페이지 로드 시 바로 확인
    const savedReceiptID = localStorage.getItem("kakao_receipt_id");
    const authInProgress = localStorage.getItem("kakao_auth_in_progress");

    if (savedReceiptID && authInProgress === "true") {
      setReceiptID(savedReceiptID);
      setCheckingStatus(true);

      // 타이머 시작
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      timerIntervalRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);

      setShowTimer(true);

      // 페이지 로드 시 바로 인증 상태 체크
      const checkStatus = async () => {
        try {
          const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
          if (!accessToken) {
            setErrorMessage("인증 토큰이 만료되었습니다. 다시 로그인해주세요.");
            setShowErrorModal(true);
            setTimeout(() => {
              setShowErrorModal(false);
            }, 3000);
            return;
          }

          const response = await axios({
            method: "POST",
            url: `${API_ENDPOINTS.BASE_URL}/mobile/claim/signature/check`,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            data: {
              receiptID: savedReceiptID,
            },
          });

          if (response.status === 200) {
            navigateToNextPage();
          }
        } catch (error) {
          if (error.name === "AbortError" || axios.isCancel(error)) {
            return;
          }
        }
      };

      checkStatus();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", () => {});

      // 컴포넌트 언마운트 시 펜딩 상태 초기화
      pendingCheckRef.current = false;
      if (pendingCheckTimeoutRef.current) {
        clearTimeout(pendingCheckTimeoutRef.current);
      }
    };
  }, []);

  // elapsedTime이 변경될 때마다 상태 확인 실행
  useEffect(() => {
    const idToCheck = receiptID || localStorage.getItem("kakao_receipt_id");
    const authInProgress = localStorage.getItem("kakao_auth_in_progress");

    if (idToCheck && authInProgress === "true" && elapsedTime > 0) {
      // 자동 체크 API 호출 시 에러 알림 비활성화
      checkSignatureStatus(idToCheck, true, false);
    }
  }, [elapsedTime]);

  // receiptID가 설정되면 타이머 시작 및 상태 체크 시작
  useEffect(() => {
    if (receiptID || localStorage.getItem("kakao_receipt_id")) {
      // 타이머 표시 시작
      setShowTimer(true);
      setElapsedTime(0);

      // 타이머 인터벌 설정
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      timerIntervalRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);

      // 최대 5분(300초) 후에 자동으로 타이머 중지 및 에러 표시
      setTimeout(() => {
        if (!verificationSuccessRef.current) {
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }

          // 에러 모달 표시
          setErrorMessage(
            "카카오 인증이 완료되지 않았습니다. 다시 시도해주세요."
          );
          setShowTimer(false);
          setShowErrorModal(true);

          setTimeout(() => {
            setShowErrorModal(false);
            setIsAuthenticating(false); // 인증 UI 상태 초기화
          }, 3000);

          // 인증 상태 초기화
          setIsAuthenticating(false);
          setCheckingStatus(false);
          localStorage.removeItem("kakao_auth_in_progress");
          localStorage.removeItem("kakao_receipt_id");
          localStorage.removeItem("kakao_auth_timestamp");
        }
      }, 300000); // 5분(300초) 타임아웃
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [receiptID]);

  // 폼 데이터 변경 시 유효성 검사
  useEffect(() => {
    const { name, phone, birthdate } = formData;

    // 각 필드 유효성 검사
    const nameValid = name.trim() !== "";
    const phoneValid = phoneRegex.test(phone);
    const birthdateValid = birthdateRegex.test(birthdate);

    // 에러 상태 업데이트 (폼 제출 시도 후에만)
    if (isSubmitAttempted) {
      setErrors({
        name: !nameValid,
        phone: !phoneValid,
        birthdate: !birthdateValid,
      });
    }

    // 모든 필드가 유효한지 확인
    const isValid = nameValid && phoneValid && birthdateValid;
    setIsFormValid(isValid);
  }, [formData, isSubmitAttempted]);

  // 입력 필드 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // 필드별 유효성 검사 및 변환
    let processedValue = value;

    if (name === "phone") {
      // 숫자만 허용
      processedValue = value.replace(/[^0-9]/g, "").slice(0, 11);
    } else if (name === "birthdate") {
      // 숫자만 허용하고 8자리로 제한
      processedValue = value.replace(/[^0-9]/g, "").slice(0, 8);
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    // 입력 중에 에러 상태 업데이트 (사용자가 타이핑 중일 때 에러 표시를 지우기 위함)
    if (isSubmitAttempted) {
      setErrors({
        ...errors,
        [name]: false,
      });
    }
  };

  // 입력 필드에 진동 애니메이션 적용
  const applyShakeAnimation = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.classList.add("shake-animation");
      setTimeout(() => {
        inputRef.current?.classList.remove("shake-animation");
      }, 500);
    }
  };

  // 서명 상태 확인 함수 개선
  const checkSignatureStatus = async (
    receiptID,
    shouldTryVerify = false,
    showError = true
  ) => {
    if (!receiptID) {
      if (showError) {
        setErrorMessage("인증 정보가 없습니다. 다시 시도해주세요.");
        setShowErrorModal(true);
        setTimeout(() => {
          setShowErrorModal(false);
        }, 3000);
      }
      return false;
    }

    if (verificationSuccessRef.current) {
      return true;
    }

    try {
      // 이전 요청 중단
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 새 컨트롤러 생성
      abortControllerRef.current = new AbortController();

      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        if (showError) {
          setErrorMessage("인증 토큰이 만료되었습니다. 다시 로그인해주세요.");
          setShowErrorModal(true);
          setTimeout(() => {
            setShowErrorModal(false);
          }, 3000);
        }
        return false;
      }

      // 타임아웃 설정 (2초)
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        if (showError) {
          setErrorMessage("카카오톡에서 인증을 먼저 완료해주세요.");
          setShowErrorModal(true);
          setTimeout(() => {
            setShowErrorModal(false);
          }, 3000);
        }
      }, 2000);

      // 1. check API 호출
      const response = await axios({
        method: "POST",
        url: `${API_ENDPOINTS.BASE_URL}/mobile/claim/signature/check`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: {
          receiptID: receiptID,
        },
      });

      // 타임아웃 클리어 (응답 받음)
      clearTimeout(timeoutId);

      if (response.status === 200) {
        navigateToNextPage();
        return true;
      }

      // Pending 상태 (202) 처리
      if (response.status === 202) {
        // 펜딩 상태 저장
        pendingCheckRef.current = true;

        // 이전 타임아웃 클리어
        if (pendingCheckTimeoutRef.current) {
          clearTimeout(pendingCheckTimeoutRef.current);
        }

        // 2초 후에 다시 체크
        pendingCheckTimeoutRef.current = setTimeout(() => {
          if (pendingCheckRef.current) {
            checkSignatureStatus(receiptID, true, true);
          }
        }, 2000);

        return false;
      }

      // verify API 호출 타임아웃 설정
    } catch (error) {
      // AbortError는 의도적인 중단이므로 에러가 아님
      if (error.name === "AbortError" || axios.isCancel(error)) {
        return false;
      }

      const savedReceiptID = localStorage.getItem("kakao_receipt_id");
      if (savedReceiptID) {
        checkSignatureStatus(savedReceiptID, true, true);
      }

      try {
        const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

        const response = await axios({
          method: "POST",
          url: `${API_ENDPOINTS.BASE_URL}/mobile/claim/signature/check`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          data: {
            receiptID: receiptID,
          },
        });
      } catch {
        // setErrorMessage(
        //   "카카오 인증이 완료되지 않았습니다. 다시 시도해주세요."
        // );
        // setShowErrorModal(true);
        // setTimeout(() => {
        //   setShowErrorModal(false);
        // }, 3000);
      } finally {
        navigateToNextPage();
      }

      // setErrorMessage(`네트워크 오류가 발생했습니다: ${error.message}`);
      // setShowErrorModal(true);
      // setTimeout(() => {
      //   setShowErrorModal(false);
      // }, 3000);
      return false;
    }
  };

  // 성공 시 다음 페이지로 이동하는 함수 분리
  const navigateToNextPage = () => {
    // 이미 처리된 경우 중복 실행 방지
    if (verificationSuccessRef.current) return;

    // 성공 표시
    verificationSuccessRef.current = true;

    // 타이머/인터벌 정리
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // UI 상태 정리
    // setShowTimer(false);
    // setCheckingStatus(false);
    // setIsAuthenticating(false);

    // 로컬 스토리지 인증 상태 초기화
    localStorage.removeItem("kakao_receipt_id");
    localStorage.removeItem("kakao_auth_in_progress");
    localStorage.removeItem("kakao_auth_timestamp");

    // 페이지 이동
    setTimeout(() => {
      navigate("/id-card-submit");
    }, 0);
  };

  // 시간 포맷팅 함수 (초 -> 분:초)
  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handleKakaoAuth = () => {
    setIsSubmitAttempted(true);

    const { name, phone, birthdate } = formData;

    // 유효성 검사
    const nameValid = name.trim() !== "";
    const phoneValid = phoneRegex.test(phone);
    const birthdateValid = birthdateRegex.test(birthdate);

    // 에러 상태 업데이트
    const newErrors = {
      name: !nameValid,
      phone: !phoneValid,
      birthdate: !birthdateValid,
    };

    setErrors(newErrors);

    // 유효하지 않은 필드가 있으면 진동 애니메이션 적용
    if (!nameValid) applyShakeAnimation(nameInputRef);
    if (!phoneValid) applyShakeAnimation(phoneInputRef);
    if (!birthdateValid) applyShakeAnimation(birthdateInputRef);

    // 모든 필드가 유효하면 인증 진행
    if (nameValid && phoneValid && birthdateValid) {
      // 인증 시작 시 성공 플래그 초기화
      verificationSuccessRef.current = false;
      returnTimestampRef.current = null;

      // 인증 진행중 상태 저장 및 타임스탬프 기록
      localStorage.setItem("kakao_auth_in_progress", "true");
      localStorage.setItem("kakao_auth_timestamp", Date.now().toString());

      // 이전 체크 시도 리셋
      checkAttemptsRef.current = 0;

      // API 호출을 위한 데이터 준비
      const apiData = {
        kakaoName: name,
        kakaoPhone: phone,
        kakaoBirth: birthdate,
      };

      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

      // API 호출 (axios로 변경)
      axios({
        method: "POST",
        url: `${API_ENDPOINTS.BASE_URL}/mobile/claim/signature`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        data: apiData,
        timeout: 15000,
      })
        .then((response) => {
          setIsAuthenticating(true);

          // receiptID 저장 및 상태 확인 시작
          if (response.data && response.data.receiptID) {
            setReceiptID(response.data.receiptID);
            setCheckingStatus(true);

            // 로컬 스토리지에도 receiptID 저장 (페이지 새로고침 대비)
            localStorage.setItem("kakao_receipt_id", response.data.receiptID);
          }
        })
        .catch((error) => {
          // 오류 발생 시 인증 상태 해제
          setIsAuthenticating(false);
          localStorage.removeItem("kakao_auth_in_progress");
          localStorage.removeItem("kakao_auth_timestamp");

          // 에러 모달 표시
          setErrorMessage("정보를 정확하게 입력해주세요.");
          setShowErrorModal(true);

          // 에러 모달 닫기
          setTimeout(() => {
            setShowErrorModal(false);
          }, 3000);
        });
    }
  };

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <h1>카카오 전자서명 인증</h1>
        {showErrorModal && (
          <motion.div
            className="error-modal"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <p>{errorMessage}</p>
          </motion.div>
        )}
      </motion.div>

      <div className="kakao-auth-content">
        {isAuthenticating ? (
          <div className="auth-in-progress">
            <div className="loading-spinner"></div>
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              카카오 인증 요청 중...
            </motion.h3>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              잠시만 기다려주세요. 카카오톡으로 인증 메시지가 발송되었습니다.
            </motion.p>
            {/* <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="check-auth-button-container"
            >
              <button
                className="kakao-btn active"
                onClick={() => {
                  const savedReceiptID =
                    localStorage.getItem("kakao_receipt_id");
                  if (savedReceiptID) {
                    checkSignatureStatus(savedReceiptID, true, true);
                  }
                }}
              >
                <div className="kakao-icon">
                  <img src="/kakaoLogo.png" alt="카카오 로고" />
                </div>
                <span>인증완료</span>
              </button>
            </motion.div> */}
          </div>
        ) : (
          <div className="auth-form-container">
            <div className="auth-notice">
              <p className="auth-notice-text">
                <strong>카카오톡에 등록된 정보와 동일하게 입력해주세요.</strong>
                <br />
                인증 시 입력한 정보와 카카오톡에 등록된 정보가 일치해야 인증이
                가능합니다.
              </p>
            </div>

            <div className="kakao-auth-form">
              <div className="form-field">
                <div className="label-container">
                  <label htmlFor="name">이름</label>
                  <span className="required">*</span>
                </div>
                <input
                  ref={nameInputRef}
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="이름을 입력하세요"
                  className={errors.name ? "error" : ""}
                  required
                />
                {/* {errors.name && (
                  <p className="error-message">{errorMessages.name}</p>
                )} */}
              </div>

              <div className="form-field">
                <div className="label-container">
                  <label htmlFor="phone">핸드폰 번호</label>
                  <span className="required">*</span>
                </div>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="01012345678 (-제외 11자리)"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.phone ? "error" : ""}
                  required
                />
                {/* {errors.phone && (
                  <p className="error-message">{errorMessages.phone}</p>
                )} */}
              </div>

              <div className="form-field">
                <div className="label-container">
                  <label htmlFor="birthdate">생년월일</label>
                  <span className="required">*</span>
                </div>
                <input
                  ref={birthdateInputRef}
                  type="text"
                  id="birthdate"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleInputChange}
                  placeholder="19900101 (8자리)"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength="8"
                  className={errors.birthdate ? "error" : ""}
                  required
                />
                {/* {errors.birthdate && (
                  <p className="error-message">{errorMessages.birthdate}</p>
                )} */}
              </div>
            </div>
          </div>
        )}
      </div>

      {!isAuthenticating && (
        <button
          className={`kakao-btn ${isFormValid ? "active" : "disabled"}`}
          onClick={handleKakaoAuth}
          disabled={!isFormValid}
        >
          <div className="kakao-icon">
            <img src="/kakaoLogo.png" alt="카카오 로고" />
          </div>
          <span>카카오 인증으로 동의하기</span>
        </button>
      )}
    </motion.div>
  );
}

export default KakaoAuthPage;
